import { kebabCase } from 'lodash/fp';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';

import {
  EXPIRATION_INTERVAL_LABELS,
  EXPIRATION_INTERVAL_PLACEHOLDERS,
  ExpirationIntervalNames,
  formatIntervalValue,
  parseIntervalValue,
  SIX_MONTHS_MINUTES,
} from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionExpirationsConfig/FormSectionExpirationsConfig';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { isInitialStep } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import {
  ExpirationIntervals,
  InquiryTemplateVersion,
} from 'dashboard/models/inquiry-template-version';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowActionMeta,
  WorkflowError,
  WorkflowFieldObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionCreateInquiryV1 } from 'dashboard/models/workflow-version';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useOnMount from 'lib/hooks/useOnMount';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const NO_ACCOUNT = 'NO_ACCOUNT';

interface ParamsCreateInquiryV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionCreateInquiryV1['params'];
  inquiryTemplates: WorkflowActionMeta['inquiry-templates'];
  templates: WorkflowActionMeta['templates'];
  themes: WorkflowActionMeta['themes'];
  themeSets: WorkflowActionMeta['theme-sets'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionCreateInquiryV1['params'],
    template?: TemplateFilter,
  ) => void;
}

type InquiryTemplateVersionParams = {
  'expiration-intervals': ExpirationIntervals;
  'field-schema': { key: string; label: string; type: string }[];
  'initial-step-options': { label: string; value: string }[];
};

const ParamsCreateInquiryV1: FC<ParamsCreateInquiryV1Props> = ({
  step,
  editable,
  errors,
  params,
  inquiryTemplates,
  templates,
  themes,
  trigger,
  usableObjects,
  themeSets,
  updateActionParams,
  updateStep,
}) => {
  const isPersonerd = useIsPersonerd();
  const hasInquiryTemplateViewPerms = usePermission(Permission.InquiryTemplateView);

  const themeSetOptions = useMemo(
    () =>
      themeSets.map((themeSet) => ({
        value: themeSet.token,
        label: `${themeSet.name} (${themeSet.token})`,
      })),
    [themeSets],
  );

  const templateOptions = useMemo(
    () =>
      [...inquiryTemplates, ...templates].map((tmpl) => ({ label: tmpl.name, value: tmpl.token })),
    [inquiryTemplates, templates],
  );

  const isTemplateAvailable = useMemo(
    () =>
      !params['template-id'] ||
      templateOptions.map((opt) => opt.value).includes(params['template-id']),
    [params, templateOptions],
  );

  const themeSelectable = useMemo(
    () => !!templates.find((tmpl) => tmpl.token === params['template-id']),
    [templates, params],
  );

  const themeOptions = useMemo(
    () => themes.map((theme) => ({ label: theme.name, value: theme.token })),
    [themes],
  );

  const accountOptions = useMemo(
    () => [
      ...filterUsableObjects({
        usableObjects,
        options: {
          permittedObjects: [WorkflowFieldObjectKey.Account],
          excludeNonPersonaObjects: true,
        },
      }),
    ],
    [usableObjects],
  );

  const updateCreateInquiryParams = <Key extends keyof WorkflowActionCreateInquiryV1['params']>(
    key: Key,
    value: WorkflowActionCreateInquiryV1['params'][Key],
  ) => {
    updateActionParams({
      ...params,
      [key]: value,
    });
  };

  const selectTemplate = (selectedTemplateToken: string) => {
    const themeId = inquiryTemplates.some(({ token }) => token === selectedTemplateToken)
      ? ''
      : params['theme-id'] === ''
        ? themeOptions[0].value
        : params['theme-id'];

    getTemplateVersionParams(selectedTemplateToken).then((templateVersionParams) => {
      const currentInquiryFields = params['inquiry-fields'] ?? {};
      const newInquiryFields = (templateVersionParams?.['field-schema'] ?? [])
        .map(({ key }) => key)
        .reduce<typeof currentInquiryFields>((fields, fieldKey) => {
          const kebabedKey = kebabCase(fieldKey);
          return currentInquiryFields[kebabedKey]
            ? {
                ...fields,
                [kebabedKey]: currentInquiryFields[kebabedKey],
              }
            : fields;
        }, {});

      updateActionParams(
        {
          ...params,
          'template-id': selectedTemplateToken,
          'theme-id': themeId,
          'inquiry-fields': newInquiryFields,
        },
        { type: ApiModel.Inquiry, id: selectedTemplateToken },
      );
    });
  };

  const [isFieldSchemaPanelVisible, { toggle: toggleFieldsSchemaPanel }] = useToggle(false);

  const [fetchedTemplateVersionParams, updateFetchedTemplateVersionParams] = useState<
    Record<string, InquiryTemplateVersionParams>
  >({});

  // Retrieve inquiry version params from backend or cache via same interface synchronously
  const getTemplateVersionParams: (
    templateToken: string,
  ) => Promise<InquiryTemplateVersionParams | null> = useCallback(
    async (templateToken: string) => {
      const cachedParams = fetchedTemplateVersionParams[templateToken];
      if (cachedParams) {
        return cachedParams;
      }

      const latestPublishedVersion = inquiryTemplates.find(
        ({ token }) => token === templateToken,
      )?.latestPublishedVersion;

      if (latestPublishedVersion == null) {
        return null;
      }

      return SessionClientDashboard.getResource<InquiryTemplateVersion>(
        ApiEndpoint.InquiryTemplateVersion,
        latestPublishedVersion,
      )
        .then(({ data: templateVersion }) => {
          const {
            'expiration-intervals': expirationIntervals,
            'field-schema': fieldSchema,
            steps,
          } = templateVersion.attributes;
          const fetchedTemplateVersionParams: InquiryTemplateVersionParams = {
            'expiration-intervals': expirationIntervals,
            'field-schema': fieldSchema,
            'initial-step-options': steps.filter(isInitialStep).map((step) => ({
              label: getDisplayNameForStep(step),
              value: step.name,
            })),
          };
          updateFetchedTemplateVersionParams((cachedParams) => ({
            ...cachedParams,
            [templateToken]: fetchedTemplateVersionParams,
          }));

          return fetchedTemplateVersionParams;
        })
        .catch(() => {
          return null;
        });
    },
    [fetchedTemplateVersionParams, inquiryTemplates],
  );

  useOnMount(() => {
    getTemplateVersionParams(params['template-id']);
  });

  const inquiryTemplateSelected =
    params['template-id'] && inquiryTemplates.some(({ token }) => token === params['template-id']);

  const supportedFieldSchema = useMemo(() => {
    const inquiryFieldSchema =
      fetchedTemplateVersionParams[params['template-id']]?.['field-schema'];

    return inquiryFieldSchema == null
      ? undefined
      : inquiryFieldSchema.reduce<Record<string, string>>(
          (acc, { key, label }) => ({
            ...acc,
            [kebabCase(key)]: label,
          }),
          {},
        );
  }, [fetchedTemplateVersionParams, params]);

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionCreateInquiryV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Template'
        htmlFor='params-create-inquiry__template-input'
        helpText={
          hasInquiryTemplateViewPerms
            ? isTemplateAvailable
              ? undefined
              : 'The current inquiry template is not available to you at this time.'
            : 'Your role does not have permissions to view inquiry templates.'
        }
      />
      <ReactSelect
        value={params['template-id']}
        options={templateOptions}
        onChange={selectTemplate}
        disabled={!editable || !hasInquiryTemplateViewPerms}
        placeholder={
          hasInquiryTemplateViewPerms
            ? isTemplateAvailable
              ? undefined
              : 'UNAVAILABLE'
            : 'RESTRICTED'
        }
        id='params-create-inquiry__template'
        hasError={hasErrorFor('template-id')}
      />
      {renderErrorMessageFor('template-id')}
      {themeSelectable && (
        <>
          <Spacer height={2} />
          <ParamLabel labelText='Theme' htmlFor='params-create-inquiry__theme' />
          <Select
            value={params['theme-id']}
            id='params-create-inquiry__theme'
            placeholder={themeOptions.length ? 'Select...' : 'No options'}
            disabled={!editable}
            hasError={hasErrorFor('theme-id')}
            onValueChange={(val: string) => updateCreateInquiryParams('theme-id', val)}
          >
            {themeOptions.map(({ label, value }) => (
              <Option key={value} value={value}>
                {label}
              </Option>
            ))}
          </Select>
          {renderErrorMessageFor('theme-id')}
        </>
      )}
      <Spacer height={2} />
      <ParamLabel labelText='Account' htmlFor='params-create-inquiry__account' />
      <Select
        value={params['account'] ? params['account'] : NO_ACCOUNT}
        id='params-create-inquiry__account'
        placeholder='Select...'
        disabled={!editable}
        hasError={hasErrorFor('account')}
        onValueChange={(selectedAccount) =>
          updateCreateInquiryParams(
            'account',
            selectedAccount === NO_ACCOUNT ? '' : selectedAccount,
          )
        }
      >
        <Option value={NO_ACCOUNT}>None</Option>
        {accountOptions.map(({ label, value }) => (
          <Option key={value} value={value}>
            {label}
          </Option>
        ))}
      </Select>
      {renderErrorMessageFor('account')}
      <Spacer height={2} />
      {inquiryTemplateSelected && (
        <Accordion title='Inquiry Fields' defaultExpanded>
          <Spacer height={2} />
          {isFieldSchemaPanelVisible && (
            <FieldMappingsPanel
              title='Inquiry Fields'
              usableObjects={usableObjects}
              fieldMappings={params['inquiry-fields'] ?? {}}
              presetFields={supportedFieldSchema}
              onOutsideClick={toggleFieldsSchemaPanel}
              onUpdateFieldMappings={(newFields) =>
                updateCreateInquiryParams('inquiry-fields', newFields ?? {})
              }
            />
          )}
          <FieldsList
            editable={editable && supportedFieldSchema != null}
            fieldKey='inquiry-fields'
            fields={params['inquiry-fields'] || {}}
            toggleEditPanel={toggleFieldsSchemaPanel}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
            errors={errors}
            placeholderEmptyText={
              supportedFieldSchema == null
                ? 'Inquiry has no fields configuration'
                : 'No fields selected'
            }
          />
        </Accordion>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <Spacer height={2} />
        <ParamLabel
          labelText='URL after completion'
          htmlFor='params-create-inquiry__redirect-uri'
          helpText='If using the Hosted flow, redirect to this URL after completion. The URL must be allowed by Integration setting.'
        />
        <Input
          value={params['redirect-uri']}
          onChange={(e) => updateCreateInquiryParams('redirect-uri', e.target.value)}
          disabled={!editable}
          placeholder='https://...'
          id='params-create-inquiry__redirect-uri'
          hasError={hasErrorFor('redirect-uri')}
        />
        {renderErrorMessageFor('redirect-uri')}
        {inquiryTemplateSelected && themeSetOptions.length > 0 && (
          <>
            {themeSetOptions.length > 0 && (
              <>
                <Spacer height={2} />
                <ParamLabel
                  labelText='Select a Theme (optional)'
                  htmlFor='params-create-inquiry__theme-set-input'
                />
                <ReactSelect
                  value={params['theme-set-id']}
                  id='params-create-inquiry__theme-set'
                  options={themeSetOptions}
                  disabled={!editable}
                  onChange={(value) => updateCreateInquiryParams('theme-set-id', value)}
                  isClearable
                  isSearchable
                />
              </>
            )}
            <Spacer height={2} />
            <ParamLabel labelText='Expiration intervals (minutes)' />
            {ExpirationIntervalNames.map((interval) => (
              <Fragment key={interval}>
                <Spacer height={1} />
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <ParamLabel
                    weight='normal'
                    labelText={EXPIRATION_INTERVAL_LABELS[interval]}
                    htmlFor={`params-create-inquiry__${interval}`}
                  />
                  <div style={{ marginLeft: 'auto' }}>
                    <Input
                      id={`params-create-inquiry__${interval}`}
                      size='sm'
                      type='number'
                      role='input'
                      min={0}
                      max={SIX_MONTHS_MINUTES}
                      maxLength={180}
                      minLength={180}
                      placeholder={
                        formatIntervalValue(
                          fetchedTemplateVersionParams[params['template-id']]?.[
                            'expiration-intervals'
                          ]?.[interval],
                        )?.toString() ?? EXPIRATION_INTERVAL_PLACEHOLDERS[interval]
                      }
                      value={formatIntervalValue(params[interval]) || undefined}
                      onChange={(e) =>
                        updateCreateInquiryParams(
                          interval,
                          parseIntervalValue(e.target.value) || undefined,
                        )
                      }
                    />
                  </div>
                </div>
              </Fragment>
            ))}
            {isPersonerd && (
              <>
                <Spacer height={2} />
                <ParamLabel
                  labelText='Alternate initial step'
                  htmlFor='params-create-inquiry__initial-step-name-input'
                  helpText="If multiple initial steps are configured on the template, you can select which you'd like the created inquiry to begin at"
                />
                <ReactSelect
                  value={params['initial-step-name']}
                  options={
                    fetchedTemplateVersionParams[params['template-id']]?.['initial-step-options'] ??
                    []
                  }
                  id='params-create-inquiry__initial-step-name'
                  disabled={!editable}
                  onChange={(value) => updateCreateInquiryParams('initial-step-name', value)}
                  isClearable
                />
              </>
            )}
          </>
        )}
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsCreateInquiryV1;
